import { DraftArchive } from "../components/DraftArchive";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import type { Invoice, IssueProblem, Payment } from "../lib/types";
import { api, apiUrl, errorMessage, mutate } from "../lib/api";
import {
  PAYMENT_METHOD_LABELS,
  formatCHF,
  formatIsoDate,
  formatPeriodRange,
  todayIso,
} from "../lib/format";
import { StatusBadge } from "../components/StatusBadge";
import { Modal } from "../components/Modal";

// --- Shared helpers (also used by InvoiceDetail) ----------------------------

/** "80", "80.5", "1'200.50", "80,50" -> integer rappen; null when invalid. */
export function parseChfToRappen(input: string): number | null {
  const s = input.trim().replace(/'/g, "").replace(",", ".");
  if (!/^-?\d+(\.\d{1,2})?$/.test(s)) return null;
  return Math.round(parseFloat(s) * 100);
}

/** Rappen -> "80.00" for prefiling text inputs. */
export function rappenToChfInput(rappen: number): string {
  return (rappen / 100).toFixed(2);
}

const PROBLEM_LABELS: Record<string, string> = {
  recipient_incomplete: "Empfängeradresse unvollständig",
  total_below_minimum: "Betrag muss mindestens CHF 0.01 sein",
  qr_settings_invalid: "QR-Rechnungs-Einstellungen unvollständig (IBAN/Adresse prüfen)",
  covers_inactive_period: "Deckt eine Periode eines pausierten oder beendeten Beitrags",
  number_taken: "Rechnungsnummer bereits vergeben",
  invoice_not_draft: "Rechnung ist bereits freigegeben",
  invoice_cancelled: "Rechnung wurde storniert",
};

export function problemText(p: IssueProblem): string {
  return PROBLEM_LABELS[p.code] ?? p.code;
}

/** Sammel-PDF: fetch merged PDF and open it in a new tab. */
export async function openPrintBatch(tenantId: string, invoiceIds: string[]): Promise<void> {
  const res = await fetch(apiUrl(`/tenants/${tenantId}/invoices/print-batch`), {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ invoice_ids: invoiceIds }),
  });
  if (!res.ok) throw new Error(`Sammel-PDF fehlgeschlagen (HTTP ${res.status})`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank", "noopener,noreferrer");
}

export function CoverageChips({ coverage }: { coverage: Invoice["coverage"] }) {
  if (coverage.length === 0) return null;
  return (
    <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
      <span>Deckt ab:</span>
      {coverage.map((c) => (
        <span
          key={`${c.subscription_id}:${c.period_start}`}
          className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-slate-600"
          title={c.package_name ?? undefined}
        >
          {c.member_name ? `${c.member_name} · ` : ""}
          {formatPeriodRange(c.period_start, c.period_end)}
        </span>
      ))}
    </div>
  );
}

const OPEN_STATUSES = new Set<Invoice["display_status"]>(["offen", "teilbezahlt", "ueberfaellig"]);

type FilterKey = "entwuerfe" | "offen" | "alle" | "storniert";

// --- Page --------------------------------------------------------------------

export function InvoicesPage({ tenantId }: { tenantId: string }) {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterKey>("alle");
  const defaultFilterApplied = useRef(false);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [paying, setPaying] = useState<Invoice | null>(null);
  const [issuing, setIssuing] = useState<Invoice | null>(null);
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [bulkResult, setBulkResult] = useState<{
    issued: number;
    issuedIds: string[];
    failed: { invoice_id: string; error: string; problems?: IssueProblem[] }[];
  } | null>(null);

  async function refresh() {
    try {
      const res = await api<{ invoices: Invoice[] }>(`/tenants/${tenantId}/invoices`);
      setInvoices(res.invoices);
      setLoadError(null);
      if (!defaultFilterApplied.current) {
        defaultFilterApplied.current = true;
        setFilter(res.invoices.some((i) => i.display_status === "entwurf") ? "entwuerfe" : "offen");
      }
    } catch (e) {
      setLoadError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    defaultFilterApplied.current = false;
    setLoading(true);
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  useEffect(() => {
    setSelected(new Set());
    setActionError(null);
  }, [filter]);

  const drafts = useMemo(() => invoices.filter((i) => i.display_status === "entwurf"), [invoices]);
  const open = useMemo(
    () => invoices.filter((i) => OPEN_STATUSES.has(i.display_status)),
    [invoices],
  );
  const cancelled = useMemo(
    () =>
      invoices.filter(
        (i) => i.display_status === "storniert" || i.display_status === "stornorechnung",
      ),
    [invoices],
  );
  const all = useMemo(() => [...invoices].sort((a, b) => b.created_at - a.created_at), [invoices]);

  const filteredAll = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter(
      (i) =>
        (i.number ?? "").toLowerCase().includes(q) || i.recipient_name.toLowerCase().includes(q),
    );
  }, [all, search]);

  function toggleSelected(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll(rows: Invoice[]) {
    setSelected((prev) => {
      const everySelected = rows.length > 0 && rows.every((r) => prev.has(r.id));
      return everySelected ? new Set<string>() : new Set(rows.map((r) => r.id));
    });
  }

  async function skipDraft(inv: Invoice) {
    if (
      !window.confirm(
        `Entwurf für ${inv.recipient_name} löschen und die Periode überspringen? Für diesen Zeitraum wird keine neue Rechnung erstellt.`,
      )
    )
      return;
    try {
      await mutate(`/tenants/${tenantId}/invoices/${inv.id}?skip=1`, {}, "DELETE");
      setActionError(null);
      refresh();
    } catch (e) {
      setActionError(errorMessage(e));
    }
  }

  const selectedDrafts = drafts.filter((d) => selected.has(d.id));
  const selectedDraftsTotal = selectedDrafts.reduce((sum, d) => sum + d.total_rappen, 0);
  const selectedOpen = open.filter((o) => selected.has(o.id));

  async function bulkIssue() {
    const ids = selectedDrafts.map((d) => d.id);
    try {
      const res = await mutate<{
        issued: number;
        failed: { invoice_id: string; error: string; problems?: IssueProblem[] }[];
      }>(`/tenants/${tenantId}/invoices/issue-all`, { invoice_ids: ids });
      const failedIds = new Set(res.failed.map((f) => f.invoice_id));
      setBulkResult({
        issued: res.issued,
        issuedIds: ids.filter((id) => !failedIds.has(id)),
        failed: res.failed,
      });
      setSelected(new Set());
      refresh();
    } catch (e) {
      setActionError(errorMessage(e));
    } finally {
      setBulkConfirm(false);
    }
  }

  async function printBatch(ids: string[]) {
    try {
      await openPrintBatch(tenantId, ids);
      setActionError(null);
    } catch (e) {
      setActionError(errorMessage(e));
    }
  }

  const recipientById = (id: string) => invoices.find((i) => i.id === id)?.recipient_name ?? id;

  const pills: { key: FilterKey; label: string }[] = [
    { key: "entwuerfe", label: `Entwürfe${drafts.length > 0 ? ` (${drafts.length})` : ""}` },
    { key: "offen", label: `Offen${open.length > 0 ? ` (${open.length})` : ""}` },
    { key: "alle", label: "Alle" },
    { key: "storniert", label: "Storniert" },
  ];

  return (
    <div>
      <DraftArchive tenantId={tenantId} onRestored={refresh} />
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="text-xl font-semibold tracking-tight">Rechnungen</h2>
        <button
          onClick={() => setShowNew(true)}
          className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800"
        >
          Neue Rechnung
        </button>
      </div>

      <div className="mb-5 flex flex-wrap gap-2 text-sm">
        {pills.map((p) => (
          <button
            key={p.key}
            onClick={() => setFilter(p.key)}
            className={
              "rounded-full px-3 py-1 " +
              (filter === p.key
                ? "bg-slate-900 text-white"
                : "bg-white border border-slate-200 text-slate-600 hover:text-slate-900")
            }
          >
            {p.label}
          </button>
        ))}
      </div>

      {actionError && (
        <div className="mb-4 flex items-start justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700">
          <span>{actionError}</span>
          <button onClick={() => setActionError(null)} className="text-red-400 hover:text-red-700">
            ✕
          </button>
        </div>
      )}

      {loading ? (
        <p className="text-slate-400">Lädt…</p>
      ) : loadError ? (
        <p className="text-sm text-red-600">{loadError}</p>
      ) : filter === "entwuerfe" ? (
        <>
          {drafts.length === 0 ? (
            <EmptyState text="Keine Entwürfe. Entwürfe entstehen automatisch aus den Beiträgen oder über «Neue Rechnung»." />
          ) : (
            <div className="rounded-xl border border-slate-200 bg-white">
              <div className="flex items-center gap-3 border-b border-slate-100 px-4 py-2 text-xs text-slate-500">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-slate-900"
                  checked={drafts.length > 0 && drafts.every((d) => selected.has(d.id))}
                  onChange={() => toggleAll(drafts)}
                  aria-label="Alle auswählen"
                />
                <span>Alle auswählen</span>
              </div>
              <div className="divide-y divide-slate-100">
                {drafts.map((inv) => (
                  <div key={inv.id} className="flex items-start gap-3 px-4 py-3">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 accent-slate-900"
                      checked={selected.has(inv.id)}
                      onChange={() => toggleSelected(inv.id)}
                      aria-label={`${inv.recipient_name} auswählen`}
                    />
                    <div className="min-w-0 flex-1">
                      <Link
                        to="/invoices/$invoiceId"
                        params={{ invoiceId: inv.id }}
                        className="text-sm font-medium text-slate-900 hover:underline"
                      >
                        {inv.recipient_name}
                      </Link>
                      <CoverageChips coverage={inv.coverage} />
                    </div>
                    <div className="w-24 shrink-0 text-right text-sm font-medium">
                      {formatCHF(inv.total_rappen)}
                    </div>
                    <div className="flex shrink-0 flex-wrap justify-end gap-2">
                      <Link
                        to="/invoices/$invoiceId"
                        params={{ invoiceId: inv.id }}
                        className="btn-sm"
                      >
                        Prüfen
                      </Link>
                      {inv.coverage.length > 0 && (
                        <button onClick={() => skipDraft(inv)} className="btn-sm text-slate-500">
                          Überspringen
                        </button>
                      )}
                      <button
                        onClick={() => setIssuing(inv)}
                        className="rounded-lg bg-slate-900 px-3 py-1 text-xs font-medium text-white hover:bg-slate-800"
                      >
                        Freigeben
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {selectedDrafts.length > 0 && (
            <div className="sticky bottom-4 z-10 mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-lg">
              <span className="text-sm text-slate-600">
                {selectedDrafts.length} {selectedDrafts.length === 1 ? "Rechnung" : "Rechnungen"}{" "}
                ausgewählt
              </span>
              <div className="flex gap-2">
                <button onClick={() => setSelected(new Set())} className="btn-sm">
                  Auswahl aufheben
                </button>
                <button
                  onClick={() => setBulkConfirm(true)}
                  className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800"
                >
                  {selectedDrafts.length} {selectedDrafts.length === 1 ? "Rechnung" : "Rechnungen"}{" "}
                  über {formatCHF(selectedDraftsTotal)} freigeben
                </button>
              </div>
            </div>
          )}
        </>
      ) : filter === "offen" ? (
        <>
          {open.length === 0 ? (
            <EmptyState text="Keine offenen Rechnungen." />
          ) : (
            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
              <table className="w-full min-w-[640px] text-sm">
                <thead className="bg-slate-50 text-slate-500">
                  <tr>
                    <th className="px-4 py-2.5">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-slate-900"
                        checked={open.length > 0 && open.every((o) => selected.has(o.id))}
                        onChange={() => toggleAll(open)}
                        aria-label="Alle auswählen"
                      />
                    </th>
                    <th className="px-4 py-2.5 text-left font-medium">Nummer</th>
                    <th className="px-4 py-2.5 text-left font-medium">Empfänger</th>
                    <th className="px-4 py-2.5 text-left font-medium">Datum</th>
                    <th className="px-4 py-2.5 text-left font-medium">Fällig</th>
                    <th className="px-4 py-2.5 text-left font-medium">Status</th>
                    <th className="px-4 py-2.5 text-right font-medium">Offen</th>
                    <th className="px-4 py-2.5" />
                  </tr>
                </thead>
                <tbody>
                  {open.map((inv) => (
                    <tr
                      key={inv.id}
                      className="cursor-pointer border-t border-slate-100 hover:bg-slate-50"
                      onClick={() =>
                        navigate({ to: "/invoices/$invoiceId", params: { invoiceId: inv.id } })
                      }
                    >
                      <td className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          className="h-4 w-4 accent-slate-900"
                          checked={selected.has(inv.id)}
                          onChange={() => toggleSelected(inv.id)}
                          aria-label={`${inv.recipient_name} auswählen`}
                        />
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5 font-mono text-xs">
                        {inv.number ?? "—"}
                      </td>
                      <td className="px-4 py-2.5">{inv.recipient_name}</td>
                      <td className="whitespace-nowrap px-4 py-2.5 text-slate-500">
                        {formatIsoDate(inv.invoice_date)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5 text-slate-500">
                        {inv.due_date ? formatIsoDate(inv.due_date) : "—"}
                      </td>
                      <td className="px-4 py-2.5">
                        <StatusBadge status={inv.display_status} />
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5 text-right font-medium">
                        {formatCHF(inv.balance_rappen)}
                      </td>
                      <td
                        className="whitespace-nowrap px-4 py-2.5 text-right"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button onClick={() => setPaying(inv)} className="btn-sm">
                          Zahlung erfassen
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {selectedOpen.length > 0 && (
            <div className="sticky bottom-4 z-10 mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-lg">
              <span className="text-sm text-slate-600">
                {selectedOpen.length} {selectedOpen.length === 1 ? "Rechnung" : "Rechnungen"}{" "}
                ausgewählt
              </span>
              <div className="flex gap-2">
                <button onClick={() => setSelected(new Set())} className="btn-sm">
                  Auswahl aufheben
                </button>
                <button
                  onClick={() => printBatch(selectedOpen.map((o) => o.id))}
                  className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800"
                >
                  Sammel-PDF drucken
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          {filter === "alle" && (
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Suche nach Nummer oder Empfänger…"
              className="input mb-4 max-w-sm"
            />
          )}
          <InvoiceTable
            invoices={filter === "alle" ? filteredAll : cancelled}
            emptyText={
              filter === "alle"
                ? search
                  ? "Keine Treffer."
                  : "Noch keine Rechnungen."
                : "Keine stornierten Rechnungen."
            }
            onOpen={(id) => navigate({ to: "/invoices/$invoiceId", params: { invoiceId: id } })}
          />
        </>
      )}

      {showNew && (
        <NewInvoiceModal
          tenantId={tenantId}
          onClose={() => setShowNew(false)}
          onCreated={(id) => {
            setShowNew(false);
            navigate({ to: "/invoices/$invoiceId", params: { invoiceId: id } });
          }}
        />
      )}

      {paying && (
        <PaymentFormModal
          title={`Zahlung erfassen — ${paying.number ?? paying.recipient_name}`}
          initial={{
            amount_rappen: paying.balance_rappen,
            paid_on: todayIso(),
            method: "bank",
            note: null,
          }}
          submitLabel="Zahlung erfassen"
          onClose={() => setPaying(null)}
          onSubmit={async (v) => {
            await mutate(`/tenants/${tenantId}/invoices/${paying.id}/payments`, v);
            setPaying(null);
            refresh();
          }}
        />
      )}

      {issuing && (
        <IssueModal
          tenantId={tenantId}
          invoice={issuing}
          onClose={() => setIssuing(null)}
          onIssued={() => {
            setIssuing(null);
            refresh();
          }}
        />
      )}

      {bulkConfirm && (
        <Modal title="Rechnungen freigeben" onClose={() => setBulkConfirm(false)}>
          <p className="text-sm text-slate-600">
            {selectedDrafts.length} {selectedDrafts.length === 1 ? "Rechnung" : "Rechnungen"} über
            insgesamt <span className="font-medium">{formatCHF(selectedDraftsTotal)}</span>{" "}
            freigeben? Jede Rechnung erhält eine Nummer und ein fertiges PDF.
          </p>
          <p className="mt-2 text-sm font-medium text-slate-900">
            Danach nur per Storno korrigierbar.
          </p>
          <div className="mt-5 flex justify-end gap-2">
            <button onClick={() => setBulkConfirm(false)} className="btn-sm px-3 py-1.5 text-sm">
              Abbrechen
            </button>
            <BusyButton onClick={bulkIssue} label="Freigeben" busyLabel="Freigeben…" />
          </div>
        </Modal>
      )}

      {bulkResult && (
        <Modal title="Freigabe abgeschlossen" onClose={() => setBulkResult(null)}>
          <p className="text-sm text-emerald-700">
            {bulkResult.issued} {bulkResult.issued === 1 ? "Rechnung" : "Rechnungen"} freigegeben.
          </p>
          {bulkResult.failed.length > 0 && (
            <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm">
              <p className="font-medium text-red-700">{bulkResult.failed.length} fehlgeschlagen:</p>
              <ul className="mt-1 list-disc pl-5 text-red-700">
                {bulkResult.failed.map((f) => (
                  <li key={f.invoice_id}>
                    {recipientById(f.invoice_id)}:{" "}
                    {f.problems && f.problems.length > 0
                      ? f.problems.map(problemText).join(", ")
                      : (PROBLEM_LABELS[f.error] ?? f.error)}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="mt-5 flex justify-end gap-2">
            <button onClick={() => setBulkResult(null)} className="btn-sm px-3 py-1.5 text-sm">
              Schliessen
            </button>
            {bulkResult.issuedIds.length > 0 && (
              <button
                onClick={() => printBatch(bulkResult.issuedIds)}
                className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm text-white hover:bg-slate-800"
              >
                Sammel-PDF drucken
              </button>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}

// --- List building blocks ------------------------------------------------------

function EmptyState({ text }: { text: string }) {
  return (
    <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center text-slate-500">
      {text}
    </div>
  );
}

function InvoiceTable({
  invoices,
  emptyText,
  onOpen,
}: {
  invoices: Invoice[];
  emptyText: string;
  onOpen: (id: string) => void;
}) {
  if (invoices.length === 0) return <EmptyState text={emptyText} />;
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
      <table className="w-full min-w-[640px] text-sm">
        <thead className="bg-slate-50 text-slate-500">
          <tr>
            <th className="px-4 py-2.5 text-left font-medium">Nummer</th>
            <th className="px-4 py-2.5 text-left font-medium">Empfänger</th>
            <th className="px-4 py-2.5 text-left font-medium">Datum</th>
            <th className="px-4 py-2.5 text-left font-medium">Status</th>
            <th className="px-4 py-2.5 text-right font-medium">Betrag</th>
          </tr>
        </thead>
        <tbody>
          {invoices.map((inv) => (
            <tr
              key={inv.id}
              className="cursor-pointer border-t border-slate-100 hover:bg-slate-50"
              onClick={() => onOpen(inv.id)}
            >
              <td className="whitespace-nowrap px-4 py-2.5 font-mono text-xs">
                {inv.number ?? "Entwurf"}
              </td>
              <td className="px-4 py-2.5">{inv.recipient_name}</td>
              <td className="whitespace-nowrap px-4 py-2.5 text-slate-500">
                {formatIsoDate(inv.invoice_date)}
              </td>
              <td className="px-4 py-2.5">
                <StatusBadge status={inv.display_status} />
              </td>
              <td className="whitespace-nowrap px-4 py-2.5 text-right">
                {formatCHF(inv.total_rappen)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BusyButton({
  onClick,
  label,
  busyLabel,
  disabled,
  title,
}: {
  onClick: () => Promise<void> | void;
  label: string;
  busyLabel: string;
  disabled?: boolean;
  title?: string;
}) {
  const [busy, setBusy] = useState(false);
  return (
    <button
      disabled={busy || disabled}
      title={title}
      onClick={async () => {
        setBusy(true);
        try {
          await onClick();
        } finally {
          setBusy(false);
        }
      }}
      className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
    >
      {busy ? busyLabel : label}
    </button>
  );
}

// --- Issue modal (validate first; shared with the detail page) ------------------

export function IssueModal({
  tenantId,
  invoice,
  onClose,
  onIssued,
}: {
  tenantId: string;
  invoice: Invoice;
  onClose: () => void;
  onIssued: (issued: Invoice) => void;
}) {
  const [problems, setProblems] = useState<IssueProblem[] | null>(null);
  const [number, setNumber] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api<{ problems: IssueProblem[] }>(`/tenants/${tenantId}/invoices/${invoice.id}/validate`)
      .then((r) => setProblems(r.problems))
      .catch((e) => setError(errorMessage(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, invoice.id]);

  const errors = (problems ?? []).filter((p) => p.level === "error");
  const warnings = (problems ?? []).filter((p) => p.level === "warning");

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const body = number.trim() ? { manual_number: number.trim() } : {};
      const issued = await mutate<Invoice>(
        `/tenants/${tenantId}/invoices/${invoice.id}/issue`,
        body,
      );
      onIssued(issued);
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  }

  return (
    <Modal title="Rechnung freigeben" onClose={onClose}>
      <p className="text-sm text-slate-600">
        Rechnung für <span className="font-medium">{invoice.recipient_name}</span> über{" "}
        <span className="font-medium">{formatCHF(invoice.total_rappen)}</span> freigeben?
      </p>
      <p className="mt-2 text-sm font-medium text-slate-900">
        Danach nicht mehr änderbar — Korrektur nur per Storno.
      </p>

      {problems === null && !error && <p className="mt-3 text-sm text-slate-400">Prüfe…</p>}
      {errors.length > 0 && (
        <ul className="mt-3 list-disc rounded-lg border border-red-200 bg-red-50 py-2 pl-7 pr-3 text-sm text-red-700">
          {errors.map((p, i) => (
            <li key={i}>{problemText(p)}</li>
          ))}
        </ul>
      )}
      {warnings.length > 0 && (
        <ul className="mt-3 list-disc rounded-lg border border-amber-200 bg-amber-50 py-2 pl-7 pr-3 text-sm text-amber-700">
          {warnings.map((p, i) => (
            <li key={i}>{problemText(p)}</li>
          ))}
        </ul>
      )}
      {errors.length > 0 && (
        <p className="mt-2 text-xs text-slate-500">
          Behebe die Fehler im Entwurf, bevor du freigibst.
        </p>
      )}

      <label className="mt-4 block">
        <span className="text-xs font-medium text-slate-500">Rechnungsnummer</span>
        <input
          value={number}
          onChange={(e) => setNumber(e.target.value)}
          placeholder="automatisch"
          className="input mt-1"
        />
      </label>

      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

      <div className="mt-5 flex justify-end gap-2">
        <button onClick={onClose} className="btn-sm px-3 py-1.5 text-sm">
          Abbrechen
        </button>
        <button
          disabled={busy || problems === null || errors.length > 0}
          onClick={submit}
          className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
        >
          {busy ? "Freigeben…" : "Freigeben"}
        </button>
      </div>
    </Modal>
  );
}

// --- Payment modal (shared with the detail page) -------------------------------

export function PaymentFormModal({
  title,
  initial,
  submitLabel,
  onClose,
  onSubmit,
}: {
  title: string;
  initial: {
    amount_rappen: number;
    paid_on: string;
    method: Payment["method"];
    note: string | null;
  };
  submitLabel: string;
  onClose: () => void;
  onSubmit: (v: {
    amount_rappen: number;
    paid_on: string;
    method: Payment["method"];
    note: string | null;
  }) => Promise<void>;
}) {
  const [amount, setAmount] = useState(rappenToChfInput(initial.amount_rappen));
  const [paidOn, setPaidOn] = useState(initial.paid_on);
  const [method, setMethod] = useState<Payment["method"]>(initial.method);
  const [note, setNote] = useState(initial.note ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const parsed = parseChfToRappen(amount);
  const valid = parsed !== null && parsed !== 0 && paidOn.length > 0;

  async function submit() {
    if (!valid || parsed === null) return;
    setBusy(true);
    setError(null);
    try {
      await onSubmit({
        amount_rappen: parsed,
        paid_on: paidOn,
        method,
        note: note.trim() || null,
      });
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  }

  return (
    <Modal title={title} onClose={onClose}>
      <label className="block">
        <span className="text-xs font-medium text-slate-500">Betrag (CHF)</span>
        <input
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className={"input mt-1 " + (parsed === null ? "border-red-300" : "")}
          inputMode="decimal"
          autoFocus
        />
      </label>
      <p className="mt-1 text-xs text-slate-400">Negativer Betrag = Rückzahlung.</p>
      <label className="mt-3 block">
        <span className="text-xs font-medium text-slate-500">Zahlungsdatum</span>
        <input
          type="date"
          value={paidOn}
          onChange={(e) => setPaidOn(e.target.value)}
          className="input mt-1"
        />
      </label>
      <label className="mt-3 block">
        <span className="text-xs font-medium text-slate-500">Zahlungsart</span>
        <select
          value={method}
          onChange={(e) => setMethod(e.target.value as Payment["method"])}
          className="input mt-1"
        >
          {(["bank", "twint", "cash", "other"] as const).map((m) => (
            <option key={m} value={m}>
              {PAYMENT_METHOD_LABELS[m]}
            </option>
          ))}
        </select>
      </label>
      <label className="mt-3 block">
        <span className="text-xs font-medium text-slate-500">Notiz</span>
        <input value={note} onChange={(e) => setNote(e.target.value)} className="input mt-1" />
      </label>

      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

      <div className="mt-5 flex justify-end gap-2">
        <button onClick={onClose} className="btn-sm px-3 py-1.5 text-sm">
          Abbrechen
        </button>
        <button
          disabled={busy || !valid}
          onClick={submit}
          className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
        >
          {busy ? "Speichern…" : submitLabel}
        </button>
      </div>
    </Modal>
  );
}

// --- New free invoice ------------------------------------------------------------

type DraftLine = { description: string; amount: string };

function NewInvoiceModal({
  tenantId,
  onClose,
  onCreated,
}: {
  tenantId: string;
  onClose: () => void;
  onCreated: (invoiceId: string) => void;
}) {
  const [name, setName] = useState("");
  const [line1, setLine1] = useState("");
  const [zip, setZip] = useState("");
  const [city, setCity] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(todayIso());
  const [dueDate, setDueDate] = useState("");
  const [lines, setLines] = useState<DraftLine[]>([{ description: "", amount: "" }]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function setLine(i: number, patch: Partial<DraftLine>) {
    setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  }

  const parsedLines = lines.map((l) => ({
    description: l.description.trim(),
    amount_rappen: parseChfToRappen(l.amount),
  }));
  const linesValid = parsedLines.every((l) => l.description && l.amount_rappen !== null);
  const total = parsedLines.reduce((sum, l) => sum + (l.amount_rappen ?? 0), 0);
  const valid = name.trim().length > 0 && lines.length > 0 && linesValid;

  async function submit() {
    if (!valid) return;
    setBusy(true);
    setError(null);
    try {
      const res = await mutate<{ invoice_id: string }>(`/tenants/${tenantId}/invoices`, {
        recipient: {
          name: name.trim(),
          line1: line1.trim() || null,
          zip: zip.trim() || null,
          city: city.trim() || null,
          country: "CH",
        },
        lines: parsedLines.map((l) => ({
          description: l.description,
          amount_rappen: l.amount_rappen as number,
        })),
        invoice_date: invoiceDate,
        ...(dueDate ? { due_date: dueDate } : {}),
      });
      onCreated(res.invoice_id);
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  }

  return (
    <Modal title="Neue Rechnung" onClose={onClose} wide>
      <p className="text-sm text-slate-500">
        Die Rechnung wird als Entwurf angelegt — du kannst sie danach prüfen und freigeben.
      </p>

      <div className="mt-4 grid grid-cols-1 gap-3">
        <label className="block">
          <span className="text-xs font-medium text-slate-500">Empfänger</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="input mt-1"
            placeholder="Name"
            autoFocus
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-500">Strasse</span>
          <input value={line1} onChange={(e) => setLine1(e.target.value)} className="input mt-1" />
        </label>
        <div className="grid grid-cols-3 gap-3">
          <label className="block">
            <span className="text-xs font-medium text-slate-500">PLZ</span>
            <input value={zip} onChange={(e) => setZip(e.target.value)} className="input mt-1" />
          </label>
          <label className="col-span-2 block">
            <span className="text-xs font-medium text-slate-500">Ort</span>
            <input value={city} onChange={(e) => setCity(e.target.value)} className="input mt-1" />
          </label>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs font-medium text-slate-500">Rechnungsdatum</span>
            <input
              type="date"
              value={invoiceDate}
              onChange={(e) => setInvoiceDate(e.target.value)}
              className="input mt-1"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-slate-500">Fällig am (optional)</span>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="input mt-1"
            />
          </label>
        </div>
      </div>

      <div className="mt-4">
        <span className="text-xs font-medium text-slate-500">Positionen</span>
        <div className="mt-1 space-y-2">
          {lines.map((l, i) => (
            <div key={i} className="flex gap-2">
              <input
                value={l.description}
                onChange={(e) => setLine(i, { description: e.target.value })}
                placeholder="Beschreibung"
                className="input flex-1"
              />
              <input
                value={l.amount}
                onChange={(e) => setLine(i, { amount: e.target.value })}
                placeholder="CHF"
                inputMode="decimal"
                className={
                  "input w-28 text-right " +
                  (l.amount && parseChfToRappen(l.amount) === null ? "border-red-300" : "")
                }
              />
              {lines.length > 1 && (
                <button
                  onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}
                  className="text-slate-400 hover:text-red-600"
                  aria-label="Position entfernen"
                >
                  ✕
                </button>
              )}
            </div>
          ))}
        </div>
        <div className="mt-2 flex items-center justify-between">
          <button
            onClick={() => setLines((ls) => [...ls, { description: "", amount: "" }])}
            className="text-sm text-slate-600 hover:text-slate-900"
          >
            + Position hinzufügen
          </button>
          <span className="text-sm font-medium">Total {formatCHF(total)}</span>
        </div>
      </div>

      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

      <div className="mt-5 flex justify-end gap-2">
        <button onClick={onClose} className="btn-sm px-3 py-1.5 text-sm">
          Abbrechen
        </button>
        <button
          disabled={busy || !valid}
          onClick={submit}
          className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
        >
          {busy ? "Erstellen…" : "Entwurf erstellen"}
        </button>
      </div>
    </Modal>
  );
}
